import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import DonutChart from '../../../components/ui/DonutChart'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { formatPercent } from '../../../lib/number'
import { BUCKET_TONE, STATUS_TONE_COLOR_VAR } from '../../actions/types'
import type { StatusDistribution, TaskOriginSplit } from '../types'
import TaskOriginSplitBar from './TaskOriginSplitBar'

interface Props {
  distribution: StatusDistribution
  /** Bölüm başlığı; iki pano da aynı bileşeni kullandığı için dışarıdan verilir (Reuse Mandate). */
  title: string
  /** Sağ üstteki "Tümünü Gör" gibi bağlantı (analiz §2.3/§2.5); iki panoda da vardır (2026-08-31). */
  hint?: ReactNode
  className?: string
  /**
   * Seçili kovalar (077 §K3; 2026-09-16'dan beri **çoklu**). Boş dizi → seçim yok. Yalnız
   * {@link Props.onSelect} ile birlikte anlamlıdır; Raporum onu vermez ve bileşen orada 068'deki gibi
   * statik kalır. Raporum tek seçimlidir ve tek elemanlı dizi geçirir — iki pano için iki ayrı seçim
   * prop'u taşımak, aynı kavramı iki kez tanımlamak olurdu.
   */
  selectedBuckets?: readonly string[]
  /**
   * **Kısmen** seçili dilimler: alt kırılımlarının bir bölümü seçili (yalnız Yönetici Raporu üretir —
   * sütun grafiğinde bir grubun tek kovası seçildiğinde). Ekran okuyucuya `aria-pressed="mixed"` olarak
   * gider; "seçili" demek yanlış, "seçili değil" demek ise eksik bilgi olurdu.
   */
  partialBuckets?: readonly string[]
  /** Bir kovaya tıklandı. Seçime ekleme/çıkarma kararı ÇAĞIRANIN işidir. */
  onSelect?: (bucket: string) => void
  /** Grafiğin altındaki tek satırlık yönerge (mockup: "Grafikteki bir statüye tıklayarak…"). */
  footNote?: ReactNode
  /**
   * Donut ile yönergenin **arasına** giren blok (079): Yönetici Raporu buraya "Durumlara Göre Detaylı
   * Dağılım" sütun grafiğini koyar. Ayrı bir karta değil aynı kartın içine girer — iki grafik aynı
   * sayıların iki farklı kırılımıdır ve arada bir kart kenarı olması onları ilgisiz gösterirdi.
   */
  children?: ReactNode
  /**
   * Statü listesinin **son satırı** olarak doğrudan / toplantı bağımlı görev dağılımı (120) — son statünün
   * hemen altında, kısa iki satır. Aynı kümenin ikinci bir kırılımıdır (toplamı halkanın merkezindeki sayı).
   */
  origin?: TaskOriginSplit
}

/**
 * "Aksiyon Durum Dağılımı" bölümü (068 — analiz §1.6 / §2.3 / §2.5; 077 §K3): donut + yanında
 * **durum adı · adet · yüzde** listesi; merkezde toplam.
 *
 * <p>Renkler **tek kaynak** eşlemeyle (`BUCKET_TONE` → `STATUS_TONE_COLOR_VAR`, statü aile paleti) gelir →
 * aynı durum panoda, aksiyon listesindeki rozette ve toplantı kartındaki çubukta aynı hue'da görünür. Adet ve yüzde
 * legend'da **metin** olarak da bulunur, yani renk tek bilgi taşıyıcısı değildir.
 *
 * <p>Merkezdeki sayı sunucudan gelen `total`dır (istemci yeniden toplamaz — sunucu otoritesi). Dilim adı
 * da sunucudan gelir: 077 ile kaynağı `REFERANS`'tan <b>mesaj paketine</b> geçti ("Gecikti Hâlâ Aksiyon
 * Bekleniyor" bir statü değil, panoya özgü bir etikettir) — istemci yine ikinci bir sabit eşleme tutmaz.
 *
 * <p><b>Seçim (077 §K3):</b> `onSelect` verildiğinde legend satırları gerçek `<button>` olur. Klavye ve
 * ekran okuyucu yolu <b>burasıdır</b>; donut dilimi yalnız fare için bir kısayoldur. `aria-pressed`
 * seçili durumu bildirir, böylece "hangi dilim seçili" bilgisi renkten bağımsız da okunabilir.
 */
export default function StatusDistributionCard({
  distribution,
  title,
  hint,
  className,
  selectedBuckets = [],
  partialBuckets = [],
  onSelect,
  footNote,
  children,
  origin,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()

  if (distribution.total === 0) {
    return (
      <SectionCard title={title} className={className}>
        <EmptyState
          icon="pi pi-chart-pie"
          title={t('home.distribution.emptyTitle')}
          description={t('home.distribution.emptyDesc')}
        />
      </SectionCard>
    )
  }

  const colorOf = (bucket: string) => STATUS_TONE_COLOR_VAR[BUCKET_TONE[bucket] ?? 'neutral']
  /** Satırın seçim hâline karşılık gelen değiştirici sınıf (yoksa boş dizge). */
  const itemState = (selected: boolean, partial: boolean) =>
    selected ? ' home-dist-item--on' : partial ? ' home-dist-item--partial' : ''
  const selectable = onSelect != null

  return (
    <SectionCard title={title} hint={hint} className={`home-dist${className ? ` ${className}` : ''}`}>
      <div className="home-dist-body">
        <DonutChart
          ariaLabel={title}
          total={distribution.total}
          totalLabel={t('home.distribution.total')}
          selectedKeys={selectedBuckets}
          partialKeys={partialBuckets}
          onSelect={onSelect}
          slices={distribution.slices.map((s) => ({
            key: s.bucket,
            label: s.statusName ?? s.bucket,
            value: s.count,
            color: colorOf(s.bucket),
            // 079: oran dilimin ÜSTÜNE yazılır. Legend'daki metin kalır — dar dilimlerde rozet
            // çizilmediği için orası tek okunabilir kaynak olabilir.
            badge: `%${formatPercent(s.percent, i18n.language)}`,
          }))}
        />
        <ul className="home-dist-legend">
          {distribution.slices.map((s) => {
            const selected = selectedBuckets.includes(s.bucket)
            const partial = !selected && partialBuckets.includes(s.bucket)
            const name = s.statusName ?? s.bucket
            // Mockup düzeni: renk kutusu solda, sağında İKİ satır — ad, altında "adet (%yüzde)".
            // Tek satıra sıkıştırıldığında uzun kova adları ("Gecikti Hâlâ Aksiyon Bekleniyor")
            // kırpılıyor ve hangi rengin ne olduğu okunamıyordu (kullanıcı geri bildirimi 2026-08-06).
            const content = (
              <>
                <span
                  className="home-dist-dot"
                  style={{ background: colorOf(s.bucket) }}
                  aria-hidden="true"
                />
                <span className="home-dist-text">
                  {/* `title`: ad tek satıra sabitlendiği için çok dar ekranda ellipsis'e düşebilir —
                      tam metin o durumda da erişilebilir kalır. */}
                  <span className="home-dist-name" title={name}>
                    {name}
                  </span>
                  <span className="home-dist-stats">
                    <span className="home-dist-count">{s.count}</span>
                    <span className="home-dist-pct">
                      (%{formatPercent(s.percent, i18n.language)})
                    </span>
                  </span>
                </span>
              </>
            )
            return (
              <li key={s.bucket} className={`home-dist-item${itemState(selected, partial)}`}>
                {/* Izgarayı taşıyan sınıf (`home-dist-row`) DAİMA dot+metni doğrudan saran öğede
                    durur — `<li>`'de değil. İkisine birden verildiğinde ızgaralar İÇ İÇE geçiyor,
                    buton `<li>`'nin 12px'lik renk kutusu kolonuna düşüyor ve metin kolonu 0px kalıyordu
                    (ad kırpılıp kayboluyor, değerler taşarak görünüyordu). */}
                {selectable ? (
                  <button
                    type="button"
                    className="home-dist-row home-dist-btn"
                    // Üç durumlu: tam seçili / kısmen seçili / seçili değil. `"mixed"` ekran okuyucuda
                    // "kısmen basılı" diye okunur — kısmî hâlin renkten bağımsız tek taşıyıcısı budur.
                    aria-pressed={partial ? 'mixed' : selected}
                    onClick={() => onSelect(s.bucket)}
                  >
                    {content}
                  </button>
                ) : (
                  <span className="home-dist-row">{content}</span>
                )}
              </li>
            )
          })}
          {origin && <TaskOriginSplitBar split={origin} />}
        </ul>
      </div>
      {children}
      {footNote != null && <p className="home-dist-note">{footNote}</p>}
    </SectionCard>
  )
}
